import React, { useEffect, useRef, useState } from 'react';
import { Flame, Truck, ShieldCheck, MapPin } from 'lucide-react';

/**
 * Quick animated counter that runs once the element scrolls into view.
 * Eases-out so the final number feels like a "landing", not a slot machine.
 */
const useCountUp = (target, durationMs = 1600) => {
  const [value, setValue] = useState(0);
  const ref = useRef(null);
  const startedRef = useRef(false);

  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !startedRef.current) {
            startedRef.current = true;
            const start = performance.now();
            const tick = (now) => {
              const t = Math.min(1, (now - start) / durationMs);
              const eased = 1 - Math.pow(1 - t, 3); // easeOutCubic
              setValue(Math.round(target * eased));
              if (t < 1) requestAnimationFrame(tick);
            };
            requestAnimationFrame(tick);
            obs.disconnect();
          }
        }
      },
      { threshold: 0.4 }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [target, durationMs]);

  return [ref, value];
};

const STATS = [
  {
    icon: Flame,
    target: 300,
    suffix: '+',
    label: 'Churrasqueiras vendidas',
    sublabel: 'pelo Brasil afora',
    color: 'from-amber-500 to-orange-600',
  },
  {
    icon: Truck,
    target: 27,
    suffix: ' UF',
    label: 'Entregamos em todo',
    sublabel: 'o território nacional',
    color: 'from-green-500 to-emerald-600',
  },
  {
    icon: ShieldCheck,
    target: 100,
    suffix: '%',
    label: 'Aço inox 304',
    sublabel: 'garantia de fábrica',
    color: 'from-blue-500 to-cyan-600',
  },
  {
    icon: MapPin,
    target: null,
    staticValue: 'Panambi',
    label: 'Fabricação própria',
    sublabel: 'no coração do RS',
    color: 'from-red-500 to-rose-600',
  },
];

const StatCard = ({ stat }) => {
  const Icon = stat.icon;
  const [ref, value] = useCountUp(stat.target || 0);

  return (
    <div
      ref={ref}
      data-testid={`stat-${stat.label.replace(/\s+/g, '-').toLowerCase()}`}
      className="group relative bg-stone-800/50 backdrop-blur-sm border border-stone-700/60 hover:border-amber-500/40 rounded-2xl p-6 text-center transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl hover:shadow-amber-500/10"
    >
      <div className={`w-14 h-14 mx-auto mb-4 rounded-2xl bg-gradient-to-br ${stat.color} flex items-center justify-center shadow-lg`}>
        <Icon className="w-7 h-7 text-white" strokeWidth={2.2} />
      </div>
      <div className="text-4xl sm:text-5xl font-black text-white leading-none mb-2">
        {stat.target != null ? (
          <>
            {value.toLocaleString('pt-BR')}
            <span className="text-amber-500">{stat.suffix}</span>
          </>
        ) : (
          <span className="text-amber-500 text-3xl sm:text-4xl">{stat.staticValue}</span>
        )}
      </div>
      <div className="text-stone-200 font-semibold text-sm sm:text-base leading-tight">
        {stat.label}
      </div>
      <div className="text-stone-500 text-xs mt-1">{stat.sublabel}</div>
    </div>
  );
};

const StatsBar = () => {
  return (
    <section
      id="stats"
      className="relative py-14 sm:py-20 bg-gradient-to-b from-stone-950 via-stone-900 to-stone-950 overflow-hidden"
    >
      {/* Subtle gradient orbs */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-orange-600/5 rounded-full blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <span className="text-amber-500 font-semibold text-xs sm:text-sm tracking-[0.25em] uppercase">
            Bagual em números
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mt-3">
            Confiança que <span className="text-amber-500">se conta na brasa</span>
          </h2>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {STATS.map((stat) => (
            <StatCard key={stat.label} stat={stat} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default StatsBar;
